/**
 * @fileoverview Firma del agente en la burbuja optimista del inbox.
 * Replica la regla "Incluir el nombre del agente al enviar mensaje" con el mismo formato que el backend
 * (microservicio `prefixWithAgentName`; Rails `Whatsapp::Outbound::Base#attach_agent_name` y
 * `FacebookMessages#attach_agent_name`). Así la burbuja ya sale firmada y el texto no cambia cuando llega el
 * mensaje guardado por socket.
 * @module agentSignatureUtils
 * @category Utils
 */
import { isWhatsapp } from '../../util/messageUtil';
import { PLATFORMS } from '../../constants/AppConstants';

/** Messenger e Instagram no interpretan los asteriscos: la firma va sin negrita. */
const PLAIN_SIGNATURE_PLATFORMS = new Set([
  PLATFORMS.MESSENGER,
  PLATFORMS.FACEBOOK,
  PLATFORMS.INSTAGRAM,
  'facebook_messages',
  'instagram_messages'
]);

/** Tipos con texto escrito por el agente. Audio, ubicación, carrusel y flow no se firman. */
const SIGNABLE_TYPES = new Set(['text', 'image', 'video', 'file', 'document']);

/**
 * Nombre con el que firma el backend: nombre y apellido del usuario, sin el email de respaldo que usa la
 * etiqueta de remitente. Vacío si no tiene nombre, igual que el microservicio, que entonces no firma.
 * @param {{ first_name?: string, last_name?: string }|null|undefined} retailerUser
 * @returns {string}
 */
export const agentSignatureName = (retailerUser) => [retailerUser?.first_name, retailerUser?.last_name]
  .filter(Boolean)
  .join(' ')
  .trim();

/**
 * Si el mensaje que se va a pintar lleva firma. Notas, plantillas, comentarios y canales sin firma (TikTok)
 * nunca la llevan.
 * @param {Object} params
 * @param {boolean} params.enabled - Regla `attach_agent_name` del retailer (`retailer_info`).
 * @param {string} params.platform - Plataforma de la conversación.
 * @param {string} [params.type] - Tipo de mensaje; `text` si no viene.
 * @param {boolean} [params.note] - Nota interna.
 * @param {boolean} [params.template] - Plantilla HSM.
 * @param {boolean} [params.isComment] - Hilo de comentarios.
 * @returns {boolean}
 */
export const shouldSignAgentMessage = ({ enabled, platform, type, note, template, isComment }) => Boolean(
  enabled
  && !note
  && !template
  && !isComment
  && SIGNABLE_TYPES.has(type || 'text')
  && (isWhatsapp(platform) || PLAIN_SIGNATURE_PLATFORMS.has(platform))
);

/**
 * Antepone la firma al texto: `*Nombre:*\n\n` en WhatsApp, `Nombre:\n\n` en Messenger e Instagram.
 * Devuelve el texto tal cual si está vacío o no hay nombre.
 * @param {string|undefined} text
 * @param {string} platform
 * @param {string} agentName
 * @returns {string|undefined}
 */
export const signAgentText = (text, platform, agentName) => {
  if (typeof text !== 'string' || !text.trim() || !agentName) return text;
  if (isWhatsapp(platform)) return `*${agentName}:*\n\n${text}`;
  if (PLAIN_SIGNATURE_PLATFORMS.has(platform)) return `${agentName}:\n\n${text}`;
  return text;
};
